import type { Observable } from 'rxjs';
import type { SitePageEntity } from '@common/types/site-page.type';
import type { Domain } from '@common/types/domain.type';

import { ChangeDetectionStrategy, Component, HostListener, inject, input, output } from '@angular/core';
import { AsyncPipe } from '@angular/common';

import type { LoginEduForm } from '@sites/entity-signal-form';

import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';

import { environment } from '@web-env/environment';
import { SpFormAdditionalSettingsSignalComponent } from '@components/sp-form-additional-settings-signal';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Post-submit behaviour panel. Value-in/event-out over the widget signal form's
 * `additionalFormSettings` group.
 */
@Component({
    selector: 'web-login-additional-settings',
    templateUrl: './login-additional-settings.component.html',
    styleUrls: ['./login-additional-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormAdditionalSettingsSignalComponent, AsyncPipe],
})
export class LoginAdditionalSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly isHasSuccessPageOption = input<boolean>(false);

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly domainsFacade: DomainsFacade = inject(DomainsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public readonly pages$: Observable<SitePageEntity[]> = this.pagesFacade.pages$;
    public readonly domains$: Observable<Domain[]> = this.domainsFacade.domains$;

    public readonly baseHref = environment.baseHref;
    public readonly selectedDeviceMode$ = this.layoutFacade.deviceMode$;

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
